import React from 'react';
import { t, type SupportedLanguage } from '@/i18n/index';
import './HelpModal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: SupportedLanguage;
}

/**
 * Help modal with usage tips for the chat assistant
 */
export function HelpModal({ isOpen, onClose, lang = 'bs' }: HelpModalProps) {
  if (!isOpen) return null;

  const tips = [
    {
      icon: '📦',
      title: lang === 'en' ? 'Product Classification' : 'Klasifikacija proizvoda',
      description: lang === 'en' 
        ? 'Describe your product with material, dimensions, and intended use for accurate HS code classification.'
        : 'Opišite proizvod sa materijalom, dimenzijama i namjenom za tačnu klasifikaciju HS koda.',
    },
    {
      icon: '📷',
      title: lang === 'en' ? 'Image Upload' : 'Učitavanje slike',
      description: lang === 'en'
        ? 'Attach product images by clicking 📷 or drag & drop. Supports JPEG, PNG, WebP (max 5MB).'
        : 'Priložite slike proizvoda klikom na 📷 ili drag & drop. Podržava JPEG, PNG, WebP (max 5MB).',
    },
    {
      icon: '🔗',
      title: lang === 'en' ? 'Product Links' : 'Linkovi proizvoda',
      description: lang === 'en'
        ? 'Paste a product URL and the assistant will automatically extract specifications.'
        : 'Zalijepite URL proizvoda i asistent će automatski izvući specifikacije.',
    },
    {
      icon: '💬',
      title: lang === 'en' ? 'Conversation' : 'Razgovor',
      description: lang === 'en'
        ? 'Ask follow-up questions. The assistant remembers context from previous messages.'
        : 'Postavljajte dodatna pitanja. Asistent pamti kontekst prethodnih poruka.',
    },
    {
      icon: '📋',
      title: lang === 'en' ? 'History' : 'Historija',
      description: lang === 'en'
        ? 'Access previous classifications from the sidebar. Export results as PDF.'
        : 'Pristupite prethodnim klasifikacijama iz bočne trake. Izvezite rezultate kao PDF.',
    },
    {
      icon: '⌨️',
      title: lang === 'en' ? 'Shortcuts' : 'Prečice',
      description: lang === 'en'
        ? 'Enter = Send message | Shift+Enter = New line'
        : 'Enter = Pošalji poruku | Shift+Enter = Novi red',
    },
  ];

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>
            {lang === 'en' ? '❓ How to use the assistant' : '❓ Kako koristiti asistenta'}
          </h2>
          <button className="help-modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="help-modal-content">
          {tips.map((tip, idx) => (
            <div key={idx} className="help-tip">
              <span className="help-tip-icon">{tip.icon}</span>
              <div className="help-tip-text">
                <strong>{tip.title}</strong>
                <p>{tip.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="help-modal-footer">
          <button className="help-modal-ok" onClick={onClose}>
            {lang === 'en' ? 'Got it!' : 'Razumijem!'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HelpModal;
